import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PublicHeader from '../components/PublicHeader'
import { usersAPI } from '../services/api'
import { Plus, Search, Filter, AlertCircle, Users, Star, CheckCircle, Heart, Clock, Zap, Video, MessageCircle, ArrowRight } from 'lucide-react'

// Small helper: format follower count to compact (e.g. 2.6K, 12K, 1.2M)
const formatCompact = (n) => {
  if (!n) return '0'
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '') + 'K'
  return String(n)
}

// Try to derive a preview image / video source from a portfolio item.
// Returns { kind: 'image' | 'video' | 'link' | 'none', src, title?, host? }
const getPortfolioPreview = (item) => {
  if (!item) return { kind: 'none' }
  if (item.thumbnailUrl) return { kind: 'image', src: item.thumbnailUrl }
  const url = item.videoUrl || ''
  if (!url) return { kind: 'none' }

  // YouTube → use hqdefault thumbnail
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,15})/)
  if (yt) return { kind: 'image', src: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` }

  // Direct video file (mp4, webm, mov, m4v) → use <video preload="metadata"> first frame
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|#|$)/i.test(url)) {
    return { kind: 'video', src: url }
  }

  // Onze CDN serveert /uploads/videos zonder extensie — vertrouwen op <video> tag werkt daar
  if (/\/uploads\/videos\//i.test(url)) {
    return { kind: 'video', src: url }
  }

  // Anders: het is waarschijnlijk een gewone webpagina-link (creator's website, Vimeo, etc.)
  // — geen embedbare bron, dus tonen als link-kaart.
  let host = ''
  try { host = new URL(url).hostname.replace(/^www\./, '') } catch (_) {}
  return { kind: 'link', src: url, title: item.title, host }
}

const availabilityLabel = (a) => {
  switch (a) {
    case 'now':         return { text: 'Direct beschikbaar',    bg: 'bg-emerald-50', text_color: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' }
    case 'within-day':  return { text: 'Beschikbaar binnen 24u',bg: 'bg-emerald-50', text_color: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' }
    case 'within-week': return { text: 'Binnen 1 week',         bg: 'bg-blue-50',    text_color: 'text-blue-700',    border: 'border-blue-200',    dot: 'bg-blue-500' }
    case 'within-month':return { text: 'Binnen 1 maand',        bg: 'bg-amber-50',   text_color: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500' }
    default: return null
  }
}

// Module-level herstel-cache: overleeft unmount/remount binnen de SPA-sessie, zodat
// terugkeren van een creator-profiel exact dezelfde lijst + scrollpositie toont.
let creatorsCache = null // { creators, page, hasNextPage, totalCreators, searchTerm, filters, scrollTop, ts }

// Vind de scrollbare container. In het dashboard heeft die de id "dashboard-scroll";
// als fallback (standalone /creators) zoeken we de scrollbare voorouder, anders window.
const getScrollContainer = (el) => {
  if (typeof document !== 'undefined') {
    const byId = document.getElementById('dashboard-scroll')
    if (byId) return byId
  }
  let node = el?.parentElement
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 4) return node
    node = node.parentElement
  }
  return null
}

const setScrollTop = (el, top) => {
  const sc = getScrollContainer(el)
  if (sc) sc.scrollTop = top
  else if (typeof window !== 'undefined') window.scrollTo(0, top)
}
const getScrollTop = (el) => {
  const sc = getScrollContainer(el)
  if (sc) return sc.scrollTop
  return typeof window !== 'undefined' ? window.scrollY : 0
}

const CreatorsPage = ({ onNavigate } = {}) => {
  // Bepaal eenmalig of er een verse herstel-cache is (max 10 min oud).
  const initialCacheRef = useRef(undefined)
  if (initialCacheRef.current === undefined) {
    initialCacheRef.current = (creatorsCache && Date.now() - (creatorsCache.ts || 0) < 600000) ? creatorsCache : null
  }
  const initialCache = initialCacheRef.current
  const emptyFilters = { niche: '', location: '', followers: '', availability: '' }
  const [creators, setCreators] = useState(() => initialCache?.creators || [])
  const [loading, setLoading] = useState(() => !initialCache)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState(() => initialCache?.searchTerm || '')
  const [filters, setFilters] = useState(() => initialCache?.filters || emptyFilters)
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(() => initialCache?.page || 1)
  const [hasNextPage, setHasNextPage] = useState(() => initialCache?.hasNextPage || false)
  const [totalCreators, setTotalCreators] = useState(() => initialCache?.totalCreators || 0)
  const [loadingMore, setLoadingMore] = useState(false)

  const { user, isAuthenticated } = useAuth()
  const pageRef = useRef(initialCache?.page || 1)
  const rootRef = useRef(null)
  const sentinelRef = useRef(null)
  const fetchingMoreRef = useRef(false)
  const restoredRef = useRef(!!initialCache)
  const lastScrollRef = useRef(initialCache?.scrollTop || 0)


  useEffect(() => {
    if (restoredRef.current) {
      // Eerste mount na terugkeer van een profiel: lijst is al hersteld uit cache —
      // niet opnieuw fetchen, alleen de scrollpositie terugzetten. We herhalen dit een
      // paar keer terwijl afbeeldingen inladen (die duwen de lay-out anders terug),
      // en stoppen zodra de gebruiker zelf scrollt.
      restoredRef.current = false
      const top = initialCache?.scrollTop || 0
      if (top > 0) {
        let userScrolled = false
        const sc = getScrollContainer(rootRef.current)
        const evtTarget = sc || window
        const onUserScroll = () => { userScrolled = true }
        const cleanup = () => { evtTarget.removeEventListener('wheel', onUserScroll); evtTarget.removeEventListener('touchmove', onUserScroll) }
        evtTarget.addEventListener('wheel', onUserScroll, { passive: true })
        evtTarget.addEventListener('touchmove', onUserScroll, { passive: true })
        const start = performance.now()
        // Pas de scrollpositie elke frame opnieuw toe (max 1,5s): dat "vecht" tegen de
        // lay-out die nog verschuift terwijl afbeeldingen inladen, en wint uiteindelijk.
        const tick = () => {
          if (userScrolled) { cleanup(); return }
          setScrollTop(rootRef.current, top)
          if (performance.now() - start < 1500) requestAnimationFrame(tick)
          else cleanup()
        }
        requestAnimationFrame(() => requestAnimationFrame(tick))
      }
    } else {
      // Echte eerste load of gewijzigde filters → verse page 1 ophalen.
      creatorsCache = null
      setPage(1)
      pageRef.current = 1
      fetchCreators(1)
    }

    // Poll elke 30s, alleen page 1 (niet als gebruiker meer heeft geladen)
    const intervalId = setInterval(() => {
      if (pageRef.current === 1) {
        fetchCreators(1, true) // stil verversen — geen skeleton/flikkering
      }
    }, 30000)

    return () => clearInterval(intervalId)
  }, [filters])

  // SEO: pagina is publiek — title/description voor Google
  useEffect(() => {
    document.title = 'UGC Creators vinden in Nederland & België | UGC4you'
    const meta = document.querySelector('meta[name="description"]')
    const prev = meta?.getAttribute('content')
    meta?.setAttribute('content', 'Bekijk 100+ Nederlandse en Belgische UGC creators met portfolio, tarieven en reviews. Plaats gratis een opdracht en ontvang voorstellen — je betaalt pas bij acceptatie.')
    return () => {
      document.title = 'UGC4you - Jouw Merk, Hun Stem | Authentieke User-Generated Content'
      if (prev) meta?.setAttribute('content', prev)
    }
  }, [])

  // Houd de huidige lijst-state bij en bewaar 'm (incl. scrollpositie) zodra de pagina
  // verlaten wordt (bv. naar een creator-profiel), zodat terugkeren exact terugkomt.
  const stateRef = useRef(null)
  stateRef.current = { creators, page, hasNextPage, totalCreators, searchTerm, filters }
  // Volg de scrollpositie LIVE tijdens het scrollen. Bij unmount uitlezen is onbetrouwbaar:
  // de gedeelde scroll-container is dan vaak al naar 0 gesprongen door de nieuwe view.
  useEffect(() => {
    const sc = getScrollContainer(rootRef.current)
    const el = sc || (typeof window !== 'undefined' ? window : null)
    if (!el) return
    const onScroll = () => {
      const v = sc ? sc.scrollTop : window.scrollY
      // Negeer een plotselinge sprong naar 0: die hoort bij het wisselen van view
      // (de gedeelde container reset), niet bij de gebruiker die naar boven scrollt.
      if (v === 0 && lastScrollRef.current > 200) return
      lastScrollRef.current = v
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      el.removeEventListener('scroll', onScroll)
      creatorsCache = { ...stateRef.current, scrollTop: lastScrollRef.current, ts: Date.now() }
    }
  }, [])

  const fetchCreators = async (requestedPage = 1, silent = false) => {
    // Bij infinite scroll kan de observer meerdere keren vuren voordat de state is
    // bijgewerkt; deze ref blokkeert dubbele pagina-requests synchroon.
    if (requestedPage > 1 && fetchingMoreRef.current) return
    try {
      if (requestedPage === 1) {
        if (!silent) setLoading(true) // stille 30s-refresh toont geen skeleton (geen flikkering)
      } else {
        fetchingMoreRef.current = true
        setLoadingMore(true)
      }

      // Build params for API call
      const params = {
        search: searchTerm,
        ...filters,
        page: requestedPage,
        limit: 12
      }

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key]
      })

      const response = await usersAPI.getCreators(params)

      // Transform the API response to match our component structure
      const transformedCreators = response.data.creators.map(creator => ({
        _id: creator.id,
        firstName: creator.name?.split(' ')[0] || '',
        lastName: creator.name?.split(' ').slice(1).join(' ') || '',
        displayName: creator.name,
        profilePictureUrl: creator.profilePictureUrl || '',
        bio: creator.bio || '',
        niches: creator.niches || [],
        location: creator.location || { city: '', country: 'Netherlands' },
        socialMedia: creator.socialMedia || {},
        stats: creator.stats || { completedProjects: 0, averageRating: 0 },
        pricing: creator.pricing || { baseRate: 0 },
        availability: creator.availability,
        portfolio: creator.portfolio || [],
        isAmbassador: creator.isAmbassador || false
      }))

      if (requestedPage === 1) {
        setCreators(transformedCreators)
      } else {
        setCreators(prev => [...prev, ...transformedCreators])
      }

      // Read pagination data from response
      const pagination = response.data.pagination
      if (pagination) {
        setHasNextPage(pagination.hasNextPage || false)
        setTotalCreators(pagination.totalCreators || pagination.total || 0)
      }

      setPage(requestedPage)
      pageRef.current = requestedPage
    } catch (error) {
      console.error('Error fetching creators:', error)
      setError('Er is een fout opgetreden bij het ophalen van creators')
    } finally {
      setLoading(false)
      setLoadingMore(false)
      fetchingMoreRef.current = false
    }
  }

  // Infinite scroll: zodra de sentinel onderaan de lijst in beeld komt (400px van
  // tevoren) wordt de volgende pagina opgehaald. De root is de scroll-container van
  // het dashboard als die er is, anders het venster.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage || loading) return
    const sc = getScrollContainer(rootRef.current)
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !fetchingMoreRef.current) {
        fetchCreators(pageRef.current + 1)
      }
    }, { root: sc || null, rootMargin: '400px 0px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, loading, creators.length])

  const handleSearch = async (e) => {
    e.preventDefault()
    setPage(1)
    await fetchCreators(1)
  }

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  // Moet gelijk lopen met de niches die creators zelf kunnen kiezen in hun profiel —
  // stonden er 14 niet in (o.a. pets, lifestyle, fitness), waardoor bedrijven daar
  // niet op konden filteren terwijl de creators wél bestonden.
  const niches = [
    { value: '', label: 'Alle niches' },
    { value: 'beauty', label: 'Beauty & Cosmetica' },
    { value: 'fashion', label: 'Mode & Kleding' },
    { value: 'food', label: 'Eten & Drinken' },
    { value: 'health', label: 'Gezondheid' },
    { value: 'fitness', label: 'Fitness & Sport' },
    { value: 'sports', label: 'Sport' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'travel', label: 'Reizen' },
    { value: 'home', label: 'Wonen & Tuin' },
    { value: 'diy', label: 'DIY & Klussen' },
    { value: 'pets', label: 'Huisdieren' },
    { value: 'parenting', label: 'Gezin & Opvoeding' },
    { value: 'tech', label: 'Technologie' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'automotive', label: 'Auto & Motor' },
    { value: 'finance', label: 'Financieel' },
    { value: 'business', label: 'Zakelijke Diensten' },
    { value: 'education', label: 'Educatie' },
    { value: 'art', label: 'Kunst & Design' },
    { value: 'music', label: 'Muziek' },
    { value: 'sustainability', label: 'Duurzaamheid' },
    { value: 'other', label: 'Overig' }
  ]

  const formatFollowers = (followers) => {
    if (followers >= 1000000) {
      return `${(followers / 1000000).toFixed(1)}M`
    } else if (followers >= 1000) {
      return `${(followers / 1000).toFixed(0)}K`
    }
    return followers?.toString() || '0'
  }

  const getTotalFollowers = (creator) => {
    const social = creator.socialMedia || {}
    let total = 0
    if (social.instagram?.followers) total += social.instagram.followers
    if (social.tiktok?.followers) total += social.tiktok.followers
    if (social.youtube?.subscribers) total += social.youtube.subscribers
    return total
  }

  return (
    <div ref={rootRef} className="font-['Inter'] min-h-screen bg-white text-[#0F172A] overflow-x-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-[150%] h-[150%] bg-gradient-to-br from-blue-50/60 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-[150%] h-[150%] bg-gradient-to-tl from-purple-50/60 via-transparent to-transparent rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header */}
      <PublicHeader />

      <div className="relative">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl font-black mb-2">Ontdek Creators</h1>
              <p className="text-[#475569] text-base md:text-lg">
                {totalCreators || creators.length} getalenteerde creators gevonden
              </p>
            </div>

            {isAuthenticated && (user?.role === 'bedrijf' || user?.role === 'agency') && (
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={() => onNavigate ? onNavigate('create-project') : window.location.href = '/projects/create'}
                  className="w-full sm:w-auto px-6 py-3 min-h-[48px] bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Opdracht Plaatsen
                </button>
              </div>
            )}
            {!isAuthenticated && (
              <div className="mt-4 sm:mt-0">
                <Link to="/opdracht-starten" className="w-full sm:w-auto px-6 py-3 min-h-[48px] bg-[#3EB489] rounded-xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Plaats gratis een opdracht
                </Link>
              </div>
            )}
          </div>

          {/* Search & Filters */}
          <div className="mb-6 md:mb-8">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="flex-1 min-w-0 relative">
                <Search className="w-5 h-5 text-[#475569]/50 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Zoek op naam, niche of specialisatie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full min-w-0 pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base placeholder-[#475569]/50 shadow-md"
                />
              </div>
              <div className="grid grid-cols-2 sm:flex gap-3 md:gap-4">
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  aria-expanded={showFilters}
                  className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-white border border-gray-200 rounded-2xl hover:border-[#3EB489] transition-all inline-flex items-center justify-center font-medium shadow-md"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto min-h-[48px] px-6 py-3 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] transition text-white inline-flex items-center justify-center"
                >
                  Zoeken
                </button>
              </div>
            </form>

            {/* Filters Panel */}
            {showFilters && (
              <div className="relative mb-6">
                <div className="relative p-3 md:p-6 rounded-2xl bg-white border border-gray-200 shadow-md">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Niche
                      </label>
                      <select
                        value={filters.niche}
                        onChange={(e) => handleFilterChange('niche', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm"
                      >
                        {niches.map(niche => (
                          <option key={niche.value} value={niche.value}>
                            {niche.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Locatie
                      </label>
                      <input
                        type="text"
                        placeholder="Stad of regio"
                        value={filters.location}
                        onChange={(e) => handleFilterChange('location', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm placeholder-[#475569]/50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Volgers
                      </label>
                      <select
                        value={filters.followers}
                        onChange={(e) => handleFilterChange('followers', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm"
                      >
                        <option value="">Alle bereiken</option>
                        <option value="1000-10000">1K - 10K</option>
                        <option value="10000-50000">10K - 50K</option>
                        <option value="50000-100000">50K - 100K</option>
                        <option value="100000+">100K+</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">
                        Beschikbaarheid
                      </label>
                      <select
                        value={filters.availability}
                        onChange={(e) => handleFilterChange('availability', e.target.value)}
                        className="w-full min-w-0 min-h-[44px] bg-white border border-gray-200 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-[#3EB489]/40 focus:border-[#3EB489] text-[#0F172A] text-base md:text-sm"
                      >
                        <option value="">Alle beschikbaarheid</option>
                        <option value="immediately">Direct beschikbaar</option>
                        <option value="within-week">Deze week</option>
                        <option value="within-month">Deze maand</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Creators Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8 md:pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-500">{error}</p>
          </div>
        ) : creators.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
              <Users className="w-10 h-10 text-[#3EB489]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold mb-3">Geen creators gevonden</h3>

            {/* Op locatie filteren is de meest voorkomende reden voor nul resultaten,
                terwijl afstand bij UGC juist niet uitmaakt: de creator filmt thuis.
                Daarom hier uitleggen in plaats van alleen "pas je filters aan". */}
            {filters.location ? (
              <>
                <p className="text-[#475569] mb-3 max-w-lg mx-auto leading-relaxed">
                  Er zijn geen creators in <strong className="text-[#0F172A]">{filters.location}</strong>.
                </p>
                <p className="text-[#475569] mb-8 max-w-lg mx-auto leading-relaxed">
                  Voor UGC hoeft dat geen probleem te zijn: de creator filmt thuis met jouw product.
                  Je stuurt het op en krijgt de video's digitaal terug — waar in het land diegene ook zit.
                </p>
                <button
                  onClick={() => handleFilterChange('location', '')}
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#3EB489] text-white font-semibold rounded-xl hover:bg-[#1478d4] transition"
                >
                  Zoek in heel Nederland
                </button>
              </>
            ) : (
              <p className="text-[#475569] mb-8 max-w-md mx-auto leading-relaxed">
                Probeer je zoekopdracht aan te passen of de filters te wijzigen.
              </p>
            )}
          </div>
        ) : (
          <>
          {/* Mobile: hero-image creator cards (1-col, full bleed) */}
          <div className="grid gap-3 min-[420px]:gap-4 grid-cols-1 min-[420px]:grid-cols-2 md:hidden">
            {creators.map((creator) => {
              const sm = creator.socialMedia || {}
              const platforms = [
                { key: 'tiktok',    label: 'TikTok',    bg: 'bg-black/85',                                                  followers: sm.tiktok?.followers || (typeof sm.tiktok === 'number' ? sm.tiktok : 0) },
                { key: 'instagram', label: 'Instagram', bg: 'bg-gradient-to-br from-fuchsia-500 to-purple-600',             followers: sm.instagram?.followers || 0 },
                { key: 'youtube',   label: 'YouTube',   bg: 'bg-red-600',                                                   followers: sm.youtube?.followers || 0 },
              ].filter(p => p.followers > 0)
              const a = availabilityLabel(creator.availability)
              return (
                <div
                  key={creator._id}
                  onClick={() => onNavigate ? onNavigate('creator-detail', creator._id) : window.location.href = `/creators/${creator._id}`}
                  className="block cursor-pointer group"
                >
                  <div className={`relative rounded-3xl bg-white overflow-hidden shadow-sm active:scale-[0.99] transition-all border ${creator.isAmbassador ? 'border-emerald-300' : 'border-gray-100'}`}>
                    {/* Hero image */}
                    <div className="relative aspect-[4/5] min-[420px]:aspect-[3/4] bg-gradient-to-br from-blue-400 via-indigo-400 to-purple-500 overflow-hidden">
                      {creator.profilePictureUrl ? (
                        <img src={creator.profilePictureUrl} alt={creator.displayName} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-6xl font-black text-white/85 drop-shadow">
                            {(creator.firstName || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      {/* Gradient veil */}
                      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />

                      {/* Top-left: availability */}
                      {a && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/95 backdrop-blur-md text-[#0F172A] shadow-sm">
                          <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`}></span>
                          {a.text}
                        </span>
                      )}

                      {/* Top-right: ambassador */}
                      {creator.isAmbassador && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-extrabold bg-emerald-500 text-white shadow-sm">
                          <CheckCircle className="w-3 h-3" /> AMBASSADEUR
                        </span>
                      )}

                      {/* Socials chip stack bottom-right (compact) */}
                      {platforms.length > 0 && (
                        <div className="absolute bottom-24 right-3 flex-col gap-1.5 items-end flex min-[420px]:hidden sm:flex">
                          {platforms.slice(0, 3).map(p => (
                            <span key={p.key} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold text-white ${p.bg} shadow-md`}>
                              {p.label}<span className="opacity-90">·{formatCompact(p.followers)}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Name overlay */}
                      <div className="absolute bottom-0 inset-x-0 p-4">
                        <h3 className="font-black text-lg min-[420px]:text-[15px] sm:text-lg text-white truncate leading-tight">
                          {creator.displayName || `${creator.firstName} ${creator.lastName}`}
                        </h3>
                        {creator.location?.city && (
                          <p className="text-xs text-white/85 truncate mt-0.5 font-medium">
                            📍 {creator.location.city}
                          </p>
                        )}
                        {/* Niche chips */}
                        {creator.niches?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {creator.niches.slice(0, 3).map((niche, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 backdrop-blur-md text-white border border-white/30">
                                {niche}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer: price + cta */}
                    <div className="flex items-center justify-between px-4 py-3 bg-white">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-[#94A3B8] font-bold">Vanaf</div>
                        <div className="text-base font-black text-[#0F172A] leading-tight">
                          {creator.pricing?.baseRate ? `€${creator.pricing.baseRate}` : 'n.v.t.'}
                        </div>
                      </div>
                      <div className="inline-flex items-center min-h-[40px] px-4 py-2 bg-[#3EB489] text-white rounded-full text-xs font-bold shadow-sm group-active:bg-[#359E78]">
                        Bekijk →
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop: hero-image creator cards (compact) */}
          <div className="hidden md:grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 items-stretch">
            {creators.map((creator) => {
              const sm = creator.socialMedia || {}
              const platforms = [
                { key: 'tiktok',    label: 'TikTok',    bg: 'bg-black/85',                                                  followers: sm.tiktok?.followers || (typeof sm.tiktok === 'number' ? sm.tiktok : 0) },
                { key: 'instagram', label: 'Instagram', bg: 'bg-gradient-to-br from-fuchsia-500 to-purple-600',             followers: sm.instagram?.followers || 0 },
                { key: 'youtube',   label: 'YouTube',   bg: 'bg-red-600',                                                   followers: sm.youtube?.followers || 0 },
              ].filter(p => p.followers > 0)
              const a = availabilityLabel(creator.availability)
              const portfolio = creator.portfolio || []
              return (
                <div
                  key={creator._id}
                  onClick={() => onNavigate ? onNavigate('creator-detail', creator._id) : window.location.href = `/creators/${creator._id}`}
                  className="group relative h-full cursor-pointer"
                >
                  <div className={`relative h-full flex flex-col rounded-2xl bg-white overflow-hidden border ${creator.isAmbassador ? 'border-emerald-300 ring-1 ring-emerald-100' : 'border-gray-100'} shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300`}>
                    {/* Hero image */}
                    <div className="relative aspect-[4/5] bg-gradient-to-br from-blue-400 via-indigo-400 to-purple-500 overflow-hidden">
                      {creator.profilePictureUrl ? (
                        <img
                          src={creator.profilePictureUrl}
                          alt={creator.displayName}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-7xl font-black text-white/90 drop-shadow-lg">
                            {(creator.firstName || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}

                      {/* Gradient veil */}
                      <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                      {/* Top row: availability + rating */}
                      <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
                        {a ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-white/95 backdrop-blur-md text-[#0F172A] shadow-sm">
                            <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`}></span>
                            {a.text}
                          </span>
                        ) : <span />}
                        {creator.stats?.averageRating > 0 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-white/95 backdrop-blur-md text-[#0F172A] shadow-sm">
                            <Star className="w-2.5 h-2.5 text-yellow-500 fill-current" />
                            {creator.stats.averageRating.toFixed(1)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-500/90 backdrop-blur-md text-white shadow-sm">
                            <Zap className="w-2.5 h-2.5" /> Nieuw
                          </span>
                        )}
                      </div>

                      {/* Ambassador ribbon */}
                      {creator.isAmbassador && (
                        <div className="absolute top-10 right-0 z-10">
                          <span className="inline-flex items-center gap-1 pl-1.5 pr-2.5 py-0.5 bg-emerald-500 text-white text-[10px] font-extrabold shadow-lg rounded-l-full">
                            <CheckCircle className="w-2.5 h-2.5" /> AMBASSADEUR
                          </span>
                        </div>
                      )}

                      {/* Bottom overlay: name, location, niches */}
                      <div className="absolute bottom-0 inset-x-0 p-2.5">
                        <h3 className="font-black text-sm text-white leading-tight truncate flex items-center gap-1.5">
                          {creator.displayName || `${creator.firstName} ${creator.lastName}`}
                        </h3>
                        <p className="text-[11px] text-white/85 font-medium mt-0.5 flex items-center gap-1">
                          📍 {creator.location?.city || creator.location?.country || 'Netherlands'}
                        </p>

                        {creator.niches?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {creator.niches.slice(0, 3).map((niche, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/15 backdrop-blur-md text-white border border-white/25">
                                {niche}
                              </span>
                            ))}
                            {creator.niches.length > 3 && (
                              <span className="px-1.5 py-0.5 text-[11px] font-semibold text-white/70">
                                +{creator.niches.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Body */}
                    <div className="flex flex-col flex-1 p-2.5">
                      {/* Bio */}
                      <p className={`text-xs mb-2 line-clamp-2 leading-snug ${creator.bio ? 'text-[#475569]' : 'text-[#94A3B8] italic'}`}>
                        {creator.bio || 'Deze creator heeft nog geen bio toegevoegd.'}
                      </p>

                      {/* Socials */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {platforms.length > 0 ? (
                          platforms.map(p => (
                            <span key={p.key} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-bold text-white ${p.bg}`}>
                              {p.label}<span className="opacity-85">{formatCompact(p.followers)}</span>
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-[#94A3B8] italic">Geen socials gekoppeld</span>
                        )}
                      </div>

                      {/* Portfolio thumbnail strip (3 max) */}
                      {portfolio.length > 0 && (
                        <div className="mb-2">
                          <div className="grid grid-cols-3 gap-1">
                            {Array.from({ length: 3 }).map((_, i) => {
                              const item = portfolio[i]
                              if (!item) {
                                return <div key={`p-empty-${i}`} className="aspect-[4/5] rounded-lg bg-gray-50 border border-dashed border-gray-200" />
                              }
                              const preview = getPortfolioPreview(item)
                              return (
                                <div key={i} className="relative aspect-[4/5] rounded-lg overflow-hidden bg-gray-100 group/thumb">
                                  {preview.kind === 'image' ? (
                                    <img
                                      src={preview.src}
                                      alt={item.title || 'Portfolio'}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                      onError={(e) => { e.currentTarget.style.display = 'none' }}
                                    />
                                  ) : preview.kind === 'video' ? (
                                    <video
                                      src={preview.src}
                                      muted
                                      playsInline
                                      preload="none"
                                      className="w-full h-full object-cover"
                                      onMouseEnter={(e) => { e.currentTarget.play().catch(() => {}) }}
                                      onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0 }}
                                    />
                                  ) : preview.kind === 'link' ? (
                                    <div
                                      className="w-full h-full bg-gradient-to-br from-[#3EB489] to-[#7c3aed] flex flex-col items-center justify-center text-white p-2 text-center"
                                    >
                                      <svg viewBox="0 0 24 24" className="w-5 h-5 mb-1" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                      <div className="text-[10px] font-bold leading-tight line-clamp-2">{preview.title || 'Video'}</div>
                                    </div>
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300" />
                                  )}
                                  {preview.kind !== 'link' && (
                                    /* Subtle play overlay */
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-90 group-hover/thumb:opacity-0 transition-opacity">
                                      <div className="w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md">
                                        <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-[#0F172A] ml-0.5" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* Stat strip - clean inline */}
                      <div className="flex items-center justify-between gap-1.5 mb-2.5 px-2 py-1.5 rounded-lg bg-[#F8FAFC] border border-gray-100">
                        <div className="flex items-center gap-1 text-[11px]">
                          <Users className="w-3 h-3 text-[#3EB489]" />
                          <span className="font-bold text-[#0F172A]">{formatFollowers(getTotalFollowers(creator))}</span>
                          <span className="text-[#94A3B8]">volg.</span>
                        </div>
                        <span className="w-px h-3 bg-gray-200" />
                        <div className="flex items-center gap-1 text-[11px]">
                          <CheckCircle className="w-3 h-3 text-emerald-500" />
                          <span className="font-bold text-[#0F172A]">{creator.stats?.completedProjects || 0}</span>
                          <span className="text-[#94A3B8]">proj.</span>
                        </div>
                        <span className="w-px h-3 bg-gray-200" />
                        <div className="flex items-center gap-1 text-[11px]">
                          <MessageCircle className="w-3 h-3 text-purple-500" />
                          <span className="font-bold text-[#0F172A]">{creator.stats?.responseRate ?? 100}%</span>
                        </div>
                      </div>

                      {/* Pricing + CTA */}
                      <div className="flex items-center justify-between mt-auto gap-2">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold truncate">
                            Vanaf{creator.pricing?.negotiable ? ' · onderh.' : ''}
                          </div>
                          <div className="text-sm font-black text-[#0F172A] leading-tight">
                            €{creator.pricing?.baseRate || 'n.v.t.'}
                          </div>
                        </div>

                        <button
                          onClick={(e) => { e.stopPropagation(); onNavigate ? onNavigate('creator-detail', creator._id) : window.location.href = `/creators/${creator._id}` }}
                          className="px-3 py-2 bg-[#3EB489] text-white rounded-lg font-bold text-xs hover:bg-[#359E78] shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                        >
                          Profiel →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Infinite scroll: sentinel + laadindicator (geen knop meer) */}
          {hasNextPage && (
            <div ref={sentinelRef} className="flex justify-center items-center gap-2.5 mt-10 py-6 text-sm text-[#94A3B8]">
              <div className="w-4 h-4 border-2 border-[#3EB489] border-t-transparent rounded-full animate-spin" />
              Meer creators laden…
            </div>
          )}

          {/* ===== Brug van bladeren naar plaatsen =====
              Bedrijven bekijken hier profielen en vertrekken daarna zonder iets te doen:
              vanuit de lijst was er geen zichtbare volgende stap. Deze kaart staat onderaan
              de resultaten, precies waar iemand komt die aan het doorbladeren is. */}
          {user?.role !== 'creator' && (
            <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-white border border-gray-200 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-[#0F172A] mb-2">
                    Laat creators naar jou toe komen
                  </h3>
                  <p className="text-[#475569] leading-relaxed">
                    In plaats van zelf profielen af te gaan: plaats je opdracht en creators reageren
                    zelf met een voorstel en een prijs. Plaatsen is gratis en kost twee minuten —
                    je betaalt pas op het moment dat je iemand kiest.
                  </p>
                </div>
                <Link
                  to={isAuthenticated ? '/projects/create' : '/register?intent=brief'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#3EB489] text-white font-semibold rounded-xl hover:bg-[#359E78] transition flex-shrink-0 whitespace-nowrap"
                >
                  Plaats je opdracht
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {!hasNextPage && creators.length > 0 && (
            <p className="text-center text-sm text-[#94A3B8] mt-10 py-6">
              Je hebt alle {totalCreators || creators.length} creators gezien.
            </p>
          )}
          </>
        )}
      </div>

      {/* Call to Action for non-authenticated users */}
      {!isAuthenticated && (
        <div className="relative mt-16 mb-8">
          <div className="relative max-w-4xl mx-auto text-center px-3 md:px-6 py-8 md:py-16 rounded-3xl bg-white border border-gray-200 shadow-md">
            <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6 rounded-full bg-[#3EB489] flex items-center justify-center">
              <Heart className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-black mb-4 text-[#3EB489]">
              Vind jouw perfecte creator
            </h2>
            <p className="text-base md:text-lg text-[#475569] mb-6 md:mb-8 max-w-2xl mx-auto">
              Meld je aan om contact op te nemen met creators of wordt zelf onderdeel van ons talent netwerk
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="group relative"
              >
                <div className="relative px-8 py-4 bg-[#3EB489] rounded-2xl font-semibold hover:bg-[#359E78] transition text-lg text-white">
                  Word Creator
                </div>
              </Link>
              <Link
                to="/register"
                className="group relative"
              >
                <div className="relative px-8 py-4 bg-white border-2 border-gray-200 rounded-2xl font-semibold hover:border-[#3EB489] hover:bg-purple-50 transition text-lg">
                  Hire Creators
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CreatorsPage